import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { FontSettings, DEFAULT_FONT_SETTINGS } from '../types/settings';

export type { FontSettings };

const FONT_SETTINGS_KEY = 'deumppuk_font_settings';

interface FontSettingsContextType {
  fontSettings: FontSettings;
  updateFontSettings: (settings: FontSettings) => void;
  reloadFontSettings: () => void;
}

const FontSettingsContext = createContext<FontSettingsContextType | undefined>(undefined);

export const useFontSettings = () => {
  const context = useContext(FontSettingsContext);
  if (!context) {
    throw new Error('useFontSettings must be used within FontSettingsProvider');
  }
  return context;
};

export const FontSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [fontSettings, setFontSettings] = useState<FontSettings>(DEFAULT_FONT_SETTINGS);
  const [reloadKey, setReloadKey] = useState(0);

  // localStorage에서 폰트 설정 불러오기
  const loadFontSettings = (): FontSettings => {
    try {
      const saved = localStorage.getItem(FONT_SETTINGS_KEY);
      if (saved) {
        return { ...DEFAULT_FONT_SETTINGS, ...JSON.parse(saved) };
      }
    } catch (e) {
      // console.error('폰트 설정 로드 실패:', e);
    }
    return DEFAULT_FONT_SETTINGS;
  };

  // 초기 로드
  useEffect(() => {
    const savedSettings = loadFontSettings();
    setFontSettings(savedSettings);
  }, []);

  // 폰트 설정 업데이트
  const updateFontSettings = (settings: FontSettings) => {
    setFontSettings(settings);
    try {
      localStorage.setItem(FONT_SETTINGS_KEY, JSON.stringify(settings));
    } catch (e) {
      // console.error('폰트 설정 저장 실패:', e);
    }
  };

  // 폰트 설정 리로드 (저장 후 호출)
  const reloadFontSettings = () => {
    const savedSettings = loadFontSettings();
    setFontSettings(savedSettings);
    setReloadKey(prev => prev + 1); // 리렌더링 트리거
  };

  return (
    <FontSettingsContext.Provider value={{ fontSettings, updateFontSettings, reloadFontSettings }}>
      {children}
    </FontSettingsContext.Provider>
  );
};
